import type { AxiosInstance } from 'axios';
import type { TranscribeRT } from '../types/index.js';
import type { Transcripts } from './interfaces/index.js';
import { safeJsonDate, safeJsonDateRequired } from './transformers/date.js';
import { transformDates } from './transformers/index.js';
import qs from './utils/query-string.js';

/*
export * as cleanup from './transcripts.cleanup';
export * as enhancements from './transcripts.enhancements';
export * as settings from './transcripts.settings';
export * as text from './transcripts.text';
*/

export * from './transcripts.summary.js';

export class TranscriptsClient {
  constructor(private authenticatedClient: AxiosInstance) { }

  downloadTranscriptReport = (data: Transcripts.DownloadTranscriptReport.Request): Promise<Transcripts.DownloadTranscriptReport.Response> => {
    return this.authenticatedClient.get(`/transcripts/${data.transcriptId}/report?format=${data.format}`).then(res => res.data);
  };

  saveTranscriptHighlight = ({ transcriptId, highlight }: Transcripts.SaveTranscriptHighlight.Request): Promise<Transcripts.SaveTranscriptHighlight.Response> => {
    return this.authenticatedClient.post(`/transcripts/${transcriptId}/highlights`, highlight).then(res => res.data);
  };

  removeTranscriptHighlight = ({ transcriptId, highlightId }: Transcripts.RemoveTranscriptHighlight.Request): Promise<Transcripts.RemoveTranscriptHighlight.Response> => {
    return this.authenticatedClient.delete(`/transcripts/${transcriptId}/highlights/${highlightId}`).then(res => res.data);
  };

  getTranscriptHighlights = ({ transcriptId }: Transcripts.GetTranscriptHighlights.Request): Promise<Transcripts.GetTranscriptHighlights.Response> => {
    return this.authenticatedClient.get(`/transcripts/${transcriptId}/highlights`).then(res => res.data);
  };

  getTranscriptComments = ({ transcriptId }: Transcripts.GetTranscriptComments.Request) => {
    return this.authenticatedClient.get<Transcripts.GetTranscriptComments.Response>(`/transcripts/${transcriptId}/comments`)
      .then(res => res.data)
      .then(res => {
        return {
          comments: res.comments.map(item => ({
            ...item,
            parentComment: {
              ...item.parentComment,
              createdOn: safeJsonDate(item.parentComment.createdOn),
            },
            children: item.children.map(comment => ({
              ...comment,
              createdOn: safeJsonDate(comment.createdOn),
            })),
          })),
        };
      });
  };

  saveTranscriptComment = ({ transcriptId, ...body }: Transcripts.SaveTranscriptComment.Request): Promise<TranscribeRT.Comment> => {
    return this.authenticatedClient.post(`/transcripts/${transcriptId}/comments`, body)
      .then(res => res.data)
      .then((res: Transcripts.SaveTranscriptComment.Response) => {
        return {
          ...res.comment,
          createdOn: safeJsonDateRequired(res.comment.createdOn),
        };
      });
  };

  updateTranscriptComment = ({ commentId, transcriptId, ...body }: Transcripts.UpdateTranscriptComment.Request): Promise<TranscribeRT.Comment> => {
    return this.authenticatedClient.patch(`/transcripts/${transcriptId}/comments/${commentId}`, body)
      .then(res => res.data)
      .then((res: Transcripts.UpdateTranscriptComment.Response) => {
        return {
          ...res.comment,
          createdOn: safeJsonDateRequired(res.comment.createdOn),
        };
      });
  };

  removeTranscriptComment = ({ transcriptId, commentId }: Transcripts.RemoveTranscriptComment.Request) => {
    return this.authenticatedClient.delete(`/transcripts/${transcriptId}/comments/${commentId}`);
  };

  getTranscriptTaggedMoments = ({ transcriptId, ...params }: Transcripts.GetTranscriptTaggedMoments.Request) => {
    return this.authenticatedClient.get<Transcripts.GetTranscriptTaggedMoments.Response>(`/transcripts/${transcriptId}/tags?${qs.stringify(params, { arrayFormat: 'bracket' })}`, {
      transformResponse: transformDates,
    }).then(res => res.data);
  };

  searchTranscriptTags = ({ transcriptId }: Transcripts.SearchTranscriptTags.Request) => {
    return this.authenticatedClient.get<Transcripts.SearchTranscriptTags.Response>(`/transcripts/${transcriptId}/tags/search`).then(res => res.data);
  };

  saveTaggedMoment = ({ transcriptId, ...body }: Transcripts.SaveTaggedMoment.Request) => {
    return this.authenticatedClient.patch<Transcripts.SaveTaggedMoment.Response>(`/transcripts/${transcriptId}/tags/moment`, body).then(res => res.data);
  };

  deleteTaggedMoment = ({ momentId, transcriptId }: Transcripts.DeleteTaggedMoment.Request) => {
    return this.authenticatedClient.delete(`/transcripts/${transcriptId}/tags/moment?momentId=${momentId}`);
  };

  getTranscriptSupplementalData = ({ transcriptId }: Transcripts.GetTranscriptSupplementalData.Request) => {
    return this.authenticatedClient.get<Transcripts.GetTranscriptSupplementalData.Response>(`/transcripts/${transcriptId}/supplemental-data`).then(res => res.data);
  };

  getTranscriptUploadCredentials = ({ transcriptId, newKey }: Transcripts.GetTranscriptUploadCredentials.Request) => {
    return this.authenticatedClient.get<Transcripts.GetTranscriptUploadCredentials.Response>(`/transcripts/${transcriptId}/upload-credentials${newKey ? '?newKey=true' : ''}`).then(res => res.data);
  };

  completeTranscriptRedaction = ({ transcriptId, tempTranscriptKey, redactRanges }: Transcripts.CompleteTranscriptRedaction.Request) => {
    return this.authenticatedClient.post<Transcripts.CompleteTranscriptRedaction.Response>(`/transcripts/${transcriptId}/complete-redaction`, {
      tempTranscriptKey,
      redactRanges,
    }).then(res => res.data);
  };

  getTranscriptEntities = ({ transcriptId }: Transcripts.GetTranscriptEntities.Request) => {
    return this.authenticatedClient.get<Transcripts.GetTranscriptEntities.Response>(`/transcripts/${transcriptId}/entities`).then(res => res.data);
  };

  updateTranscriptEntity = ({ entityId, transcriptId, ...data }: Transcripts.UpdateTranscriptEntity.Request) => {
    return this.authenticatedClient.put<Transcripts.UpdateTranscriptEntity.Response>(`/transcripts/${transcriptId}/entities/${entityId}`, data).then(res => res.data);
  };

  downloadTranscriptTags = ({ transcriptId, format: _format }: Transcripts.DownloadTranscriptTags.Request) => {
    return this.authenticatedClient.get<Transcripts.DownloadTranscriptTags.Response>(`/transcripts/${transcriptId}/tags/export`).then(res => res.data);
  };

  getTranscriptTagsOverview = ({ transcriptId }: Transcripts.GetTranscriptTagsOverview.Request) => {
    return this.authenticatedClient.get<Transcripts.GetTranscriptTagsOverview.Response>(`/transcripts/${transcriptId}/tags/overview`).then(res => res.data);
  };

  updateTranscriptSpeakers = ({ transcriptId, ...data }: Transcripts.UpdateTranscriptSpeakers.Request) => {
    return this.authenticatedClient.put<Transcripts.UpdateTranscriptSpeakers.Response>(`/transcripts/${transcriptId}/speakers`, data).then(res => res.data);
  };

  updateTranscriptSpeakerSelection = ({ transcriptId, ...data }: Transcripts.UpdateTranscriptSpeakerSelection.Request) => {
    return this.authenticatedClient.put<Transcripts.UpdateTranscriptSpeakerSelection.Response>(`/transcripts/${transcriptId}/speakers/selection`, data).then(res => res.data);
  };

  getTranscriptMedia = ({ transcriptId }: Transcripts.GetTranscriptMedia.Request) => {
    return this.authenticatedClient.get<Transcripts.GetTranscriptMedia.Response>(`/transcripts/${transcriptId}/media`).then(res => res.data);
  };

  downloadTagClip = ({ transcriptId, ...data }: Transcripts.DownloadConferenceTagClip.Request) => {
    return this.authenticatedClient.post<Transcripts.DownloadConferenceTagClip.Response>(`/transcripts/${transcriptId}/tags/moment/generate-clip`, data).then(res => res.data);
  };

  getMetadata = (data: Transcripts.GetMetadata.Request) => {
    return this.authenticatedClient.get<Transcripts.GetMetadata.Response>(`/transcripts/${data.transcriptId}/metadata`, {
      transformResponse: transformDates,
    }).then(res => res.data);
  };

  updateMetadata = (data: Transcripts.UpdateMetadata.Request) => {
    return this.authenticatedClient.patch(`/transcripts/${data.transcriptId}/metadata`, {
      publishedOn: data.publishedOn,
      title: data.title,
    });
  };

  updatePublishedOn = (data: Transcripts.UpdatePublishedOn.Request) => {
    return this.authenticatedClient.patch(`/transcripts/${data.transcriptId}/metadata/published-on`, {
      publishedOn: data.publishedOn,
    });
  };

  starTranscriptQuote = ({ transcriptId, ...data }: Transcripts.StarTranscriptQuote.Request): Promise<Transcripts.StarTranscriptQuote.Response> => {
    return this.authenticatedClient.post(`/transcripts/${transcriptId}/quotes`, data).then(res => res.data);
  };

  unstarTranscriptQuote = (data: Transcripts.UnstarTranscriptQuote.Request) => {
    return this.authenticatedClient.delete(`/transcripts/${data.transcriptId}/quotes/${data.quoteId}`);
  };

  downloadTranscriptClip = ({ transcriptId, ...params }: Transcripts.DownloadTranscriptClip.Request): Promise<Transcripts.DownloadTranscriptClip.Response> => {
    return this.authenticatedClient.get(`/transcripts/${transcriptId}/clip?${qs.stringify(params)}`).then(res => res.data);
  };

  updateTranscriptRangeTags = ({ transcriptId, ...params }: Transcripts.UpdateTranscriptRangeTags.Request): Promise<Transcripts.UpdateTranscriptRangeTags.Response> => {
    return this.authenticatedClient.post(`/transcripts/${transcriptId}/tags/range`, params);
  };

  getTranscriptTaggedMomentDetails = ({ transcriptId, ...params }: Transcripts.GetTaggedMomentDetails.Request): Promise<Transcripts.GetTaggedMomentDetails.Response> => {
    return this.authenticatedClient.get(`/transcripts/${transcriptId}/tags/moment-details?${qs.stringify(params)}`).then(res => res.data);
  };

  renameTranscript = ({ transcriptId, ...params }: Transcripts.UpdateTranscriptTitle.Request) => {
    return this.authenticatedClient.post(`/transcripts/${transcriptId}/rename`, params);
  };

  getSpellCheckSuggestions = ({ transcriptId, term }: Transcripts.GetSpellCheckSuggestions.Request) => {
    return this.authenticatedClient.get<Transcripts.GetSpellCheckSuggestions.Response>(`/transcripts/${transcriptId}/spell-check?term=${encodeURIComponent(term)}`).then(res => res.data);
  };

  getTranscriptExtractedData = ({ transcriptId }: Transcripts.GetTranscriptExtractedData.Request): Promise<Transcripts.GetTranscriptExtractedData.Response> => {
    return this.authenticatedClient.get<Transcripts.GetTranscriptExtractedData.Response>(`/transcripts/${transcriptId}/extracted-data`).then(res => res.data);
  };

  getExtractedAnswers = ({ transcriptId }: Transcripts.GetExtractedAnswers.Request): Promise<Transcripts.GetExtractedAnswers.Response> => {
    return this.authenticatedClient.get<Transcripts.GetExtractedAnswers.Response>(`/transcripts/${transcriptId}/extracted-answers`).then(res => res.data);
  };

  refreshExtractedAnswers = ({ transcriptId, ...body }: Transcripts.RefreshExtractedAnswers.Request): Promise<Transcripts.RefreshExtractedAnswers.Response> => {
    return this.authenticatedClient.post<Transcripts.RefreshExtractedAnswers.Response>(`/transcripts/${transcriptId}/extracted-answers/refresh`, body).then(res => res.data);
  };
}

export default TranscriptsClient;